import React, { useMemo } from "react";
import DOMPurify from "dompurify";
import {
  SIGNATURE_VARIABLE_KEY,
  SIGNATURE_DATE_VARIABLE_KEY,
} from "../variables";

// Placeholder values for the View tab only. NONE of this is ever sent — a real
// contract is filled with the job's own data by the backend at send time.
//
// Mirrors ContractAPI `constants.SAMPLE_PREVIEW_DATA`, the fixture the backend
// previews with, so this preview and the PDF preview agree.
const SAMPLE_DATA = {
  Client_Name: "John Doe",
  Client_Email: "john.doe@example.com",
  Property_Address: "123 Main St, San Francisco, CA 94102",
  // Old alias of Property_Address. View renders the stored html, so templates
  // not yet re-saved still carry this token and need a value here.
  Job_Address: "123 Main St, San Francisco, CA 94102",
  Total_Fee: "$550.00",
  Inspection_Date: "January 31, 2026",
  Service_Type: "Home Inspection",
  Agent_Name: "Jane Smith",
};

const PLACEHOLDER_REGEX = /\{([A-Za-z0-9_]+)\}/g;

// Walk text nodes under `root`, splitting each on `{Var_Name}` patterns and
// replacing matched fragments with either the signature-line block or the
// plain sample-data value (for Job Info variables). Values are NOT bolded —
// the design renders filled-in data as normal-weight text; any bold in the
// preview comes only from `<strong>` already present in the source content.
// Non-text nodes recurse.
const substituteInDom = (root) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode);
  for (const textNode of textNodes) {
    const value = textNode.nodeValue || "";
    PLACEHOLDER_REGEX.lastIndex = 0;
    if (!PLACEHOLDER_REGEX.test(value)) continue;
    PLACEHOLDER_REGEX.lastIndex = 0;

    const parent = textNode.parentNode;
    if (!parent) continue;
    const frag = document.createDocumentFragment();
    let lastIndex = 0;
    let match;
    while ((match = PLACEHOLDER_REGEX.exec(value)) !== null) {
      if (match.index > lastIndex) {
        frag.appendChild(
          document.createTextNode(value.slice(lastIndex, match.index))
        );
      }
      const key = match[1];
      if (key === SIGNATURE_VARIABLE_KEY) {
        const sig = document.createElement("div");
        sig.className = "contract-view-signature-line";
        sig.textContent = "Client signature — signed at send time";
        frag.appendChild(sig);
      } else if (key === SIGNATURE_DATE_VARIABLE_KEY) {
        // Inline date field the client fills at signing (blank until then).
        const span = document.createElement("span");
        span.className = "contract-view-sign-date";
        span.textContent = "date signed";
        frag.appendChild(span);
      } else if (SAMPLE_DATA[key] != null && SAMPLE_DATA[key] !== "") {
        frag.appendChild(document.createTextNode(SAMPLE_DATA[key]));
      } else {
        // Unknown or empty variable — surface the raw placeholder so the
        // author can see something's off.
        frag.appendChild(document.createTextNode(`[${key}]`));
      }
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < value.length) {
      frag.appendChild(document.createTextNode(value.slice(lastIndex)));
    }
    parent.replaceChild(frag, textNode);
  }
};

// Schemes allowed for rendered links. Lexical already sanitizes author URLs
// to `about:blank` on save, but this view is a `dangerouslySetInnerHTML` sink,
// so we re-check here as defense in depth against any content that reaches it
// from a less-trusted path.
const SAFE_LINK_SCHEME = /^(https?:|mailto:|tel:|sms:|#|\/|\.)/i;

// Force every link in the read-only contract to open in a new tab, with
// `rel="noopener noreferrer"` so the opened page can't reach back into this
// window. Without this, an author-inserted link would try to navigate the SPA
// in place. Unsafe schemes (e.g. `javascript:`) have their href stripped.
const openLinksInNewTab = (root) => {
  const anchors = root.querySelectorAll("a[href]");
  for (const anchor of anchors) {
    if (!SAFE_LINK_SCHEME.test(anchor.getAttribute("href") || "")) {
      anchor.removeAttribute("href");
    }
    anchor.setAttribute("target", "_blank");
    anchor.setAttribute("rel", "noopener noreferrer");
  }
};

const buildViewHtml = (sourceHtml) => {
  const div = document.createElement("div");
  div.innerHTML = sourceHtml || "";
  substituteInDom(div);
  openLinksInNewTab(div);
  // Sanitize the final markup — this is a dangerouslySetInnerHTML sink and the
  // source can include backend-injected boilerplate, not just editor output.
  // `target` is preserved so contract links still open in a new tab.
  return DOMPurify.sanitize(div.innerHTML, { ADD_ATTR: ["target"] });
};

const ViewMode = ({ contentHtml }) => {
  const html = useMemo(() => buildViewHtml(contentHtml), [contentHtml]);
  return (
    <>
      <div
        className="contract-view-doc"
        dangerouslySetInnerHTML={{ __html: html }}
      />
      <div className="contract-view-note">
        The actual contract will be sent with real inspection data.
      </div>
    </>
  );
};

export default ViewMode;
